import React, { useEffect, useCallback, useMemo } from "react";

import VerifyPopup from "../../components/verifyPopup/VerifyPopup.jsx";

import { evaluateVisibility } from "./engine/expressionEngine.js";
import { convertTemplate } from "./services/schemaAdapter.js";
import { saveDraft, popDraft } from "./services/formDataStore.js";

import useFormState from "./hooks/useFormState.js";
import useFormData from "./hooks/useFormData.js";
import useFormLifecycle from "./hooks/useFormLifecycle.js";
import useFormLoader from "./hooks/useFormLoader.js";
import useTabNavigation from "./hooks/useTabNavigation.js";
import useFormActions from "./hooks/useFormActions.js";
import useFormSubmit from "./hooks/useFormSubmit.js";
import useFileUpload from "./hooks/useFileUpload.js";

import HogeryForm from "./components/HogeryForm.jsx";

export default function TestForm(props) {
  const { state, updateState, resetWithFormChange } = useFormState();

  // Stable evaluateVisibility bound to docRef
  const evalVis = useCallback((expr) => {
    return evaluateVisibility(expr, formData.docRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formData = useFormData({ updateState, evaluateVisibility: evalVis });
  const { docRef, templateRef, settingsRef, formElementRef, clearUnvisibleData, deleteOwner, countVisibleTabs } = formData;

  const lifecycle = useFormLifecycle({ docRef, templateRef, settingsRef });
  const { execFunction, executeFunctionByUid, ExecuteMultipleFunctions, validateAndNotify } = lifecycle;

  const loader = useFormLoader({ docRef, templateRef, settingsRef, updateState });
  const { initializeComponent, loadValuesByTaskId } = loader;

  const validateConnection = useCallback(() => {
    if (!navigator.onLine) {
      console.warn("No internet connection");
    }
  }, []);

  const tabNav = useTabNavigation({
    state, updateState, countVisibleTabs, templateRef,
    executeFunctionByUid, validateConnection,
  });
  const { updatePhones } = tabNav;

  const actions = useFormActions({
    docRef, state, updateState, clearUnvisibleData, executeFunctionByUid,
  });
  const { onSaveFile, saveAndReset, deleteAndReset, hidePopUp, createUser, onSaveChanges, autoSaveDraft } = actions;

  const submit = useFormSubmit({
    docRef, templateRef, settingsRef, formElementRef,
    state, updateState,
    clearUnvisibleData, executeFunctionByUid, ExecuteMultipleFunctions,
    createUser, validateAndNotify,
  });
  const { sendForm } = submit;

  const fileUpload = useFileUpload({ docRef, updateState, updatePhones, executeFunctionByUid });
  const { handleUploadFile } = fileUpload;

  const setMessagePopupVisibility = useCallback((isVisible) => {
    updateState({ isMessagePopupVisible: isVisible });
  }, [updateState]);

  // Register save-draft callback for version-switch (called by header.js before redirect)
  useEffect(() => {
    window.__saveFormDraft = () => saveDraft(docRef.current);
    return () => { delete window.__saveFormDraft; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidMount equivalent
  useEffect(() => {
    initializeComponent(props.userData, evalVis);
    loadValuesByTaskId();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore draft after version-switch (only for new forms without a saved taskId)
  useEffect(() => {
    if (!state.isFirsTimeLoaded || state.isLoading) return;
    const hashSearch = window.location.hash.split("?")[1] || "";
    const taskId = new URLSearchParams(hashSearch).get("t");
    if (taskId) return; // server already has the data
    const data = popDraft();
    if (!data) return;
    Object.entries(data).forEach(([key, val]) => {
      if (val !== null && val !== undefined) {
        if (typeof val === "object" && !Array.isArray(val)) {
          docRef.current[key] = Object.assign({}, docRef.current[key], val);
        } else {
          docRef.current[key] = val;
        }
      }
    });
    updateState({ formHasChanged: Date.now() });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFirsTimeLoaded, state.isLoading]);

  // Run OnTemplateLoading once when template first loads
  useEffect(() => {
    if (state.isFirsTimeLoaded) {
      executeFunctionByUid("OnTemplateLoading");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFirsTimeLoaded]);

  // componentDidUpdate: RunAfterRender
  useEffect(() => {
    if (state.isFirsTimeLoaded && !state.isLoading) {
      executeFunctionByUid("RunAfterRender");
    }
  }, [state.isFirsTimeLoaded, state.isLoading, state.formHasChanged, executeFunctionByUid]);

  // URL change detection (mirrors original componentDidUpdate)
  useEffect(() => {
    if (!props.location) return;
    const params = new URLSearchParams(props.location.search);
    const currentFormId = params.get('f');
    const currentTaskId = params.get('t');

    // Only run after initial load
    if (!state.formId) return;

    if (state.formId !== currentFormId && currentFormId) {
      // Form ID changed — full reset
      const service = require('../../pages/test-form/data.js').default;
      docRef.current = service.getEmployee();
      resetWithFormChange();
      initializeComponent(props.userData, evalVis);
    } else if (!currentTaskId && state.formId === currentFormId) {
      // Task ID was removed — reset for same form
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location?.search]);

  // Apply saved theme immediately + override host app styles
  useEffect(() => {
    const savedTheme = localStorage.getItem("form_theme") || "light";
    const bg = savedTheme === "dark" ? "#07090F" : "#faf8f5";
    document.documentElement.setAttribute("data-theme", savedTheme);
    document.body.style.background = bg;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    const content = document.querySelector(".app .content");
    if (content) {
      content.style.background = bg;
      content.style.padding = "0";
      content.style.margin = "0";
      content.style.overflowX = "hidden";
      content.style.overflowY = "visible";
    }
    const layoutBody = document.querySelector(".layout-body.with-footer");
    if (layoutBody) {
      layoutBody.style.height = "auto";
      layoutBody.style.overflow = "visible";
    }

    return () => {
      document.documentElement.removeAttribute("data-theme");
      document.body.style.background = "";
      document.body.style.overflowX = "";
      document.documentElement.style.overflowX = "";
      if (content) {
        content.style.background = "";
        content.style.padding = "";
        content.style.margin = "";
        content.style.overflowX = "";
        content.style.overflowY = "";
      }
      if (layoutBody) { layoutBody.style.height = ""; layoutBody.style.overflow = ""; }
    };
  }, []);

  // Convert template to Hogery schema
  const hogerySchema = useMemo(() => {
    if (!templateRef.current?.tabs) return null;
    return convertTemplate(templateRef.current, state.dataFromDB?.FormName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFirsTimeLoaded, state.formHasChanged]);

  // Send form handler — adapted for Hogery (no DevExtreme validation group)
  const onSendForm = useCallback(() => {
    sendForm({ validationGroup: { validate: () => ({ status: "valid" }) } });
  }, [sendForm]);

  // ---- RENDER ----

  if (state.isFirsTimeLoaded && !state.isLoading && hogerySchema) {
    return (
      <div className="app">
        {/* Atmosphere effects (dark mode only — hidden via CSS for light) */}
        <div className="atmos atmos-noise" />
        <div className="atmos atmos-orb atmos-orb-1" />
        <div className="atmos atmos-orb atmos-orb-2" />
        <div className="atmos atmos-grid" />

        <HogeryForm
          schema={hogerySchema}
          docRef={docRef}
          templateRef={templateRef}
          evaluateVisibility={evalVis}
          execFunction={execFunction}
          updatePhones={updatePhones}
          state={state}
          updateState={updateState}
          onSendForm={onSendForm}
          onSaveFile={onSaveFile}
          onSaveChanges={onSaveChanges}
          autoSaveDraft={autoSaveDraft}
          formElementRef={formElementRef}
          setMessagePopupVisibility={setMessagePopupVisibility}
          deleteOwner={deleteOwner}
          handleUploadFile={handleUploadFile}
          saveAndReset={saveAndReset}
          deleteAndReset={deleteAndReset}
          executeFunctionByUid={executeFunctionByUid}
          validateConnection={validateConnection}
        />

        <VerifyPopup
          isMessagePopupVisible={state.isMessagePopupVisible}
          setMessagePopupVisibility={setMessagePopupVisibility}
          popupMessageContent={state.popupMessage}
          customFunction={() => deleteOwner(state.popupData)}
          title={state.popupTitle}
          button1Text="אשר"
          button2Text="בטל"
        />
      </div>
    );
  }

  // Loading screen
  return (
    <div className="app" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <h1 style={{ color: "var(--text)" }}>Loading...</h1>
      <div style={{ width: 48, height: 48, border: "3px solid var(--border)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
